/**
 * API utility with automatic token refresh
 */

// In dev mode, always use empty string to leverage Vite proxy (ignore VITE_API_URL if set)
// In production, use VITE_API_URL or default to localhost
const API_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3000');

export function apiFetchUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
    return normalized;
  }
  const base = API_URL || 'http://localhost:3000';
  return `${base}${normalized}`;
}

import { resolveWebSocketUrl, isJwtExpired } from '@euroasiann/shared';
export { resolveWebSocketUrl, isJwtExpired };

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken(): Promise<string | null> {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return null;
      }

      // In dev mode, use relative URL (Vite proxy will handle it)
      // In production, use absolute URL
      const url = import.meta.env.DEV 
        ? '/api/v1/auth/refresh'
        : `${API_URL || 'http://localhost:3000'}/api/v1/auth/refresh`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      if (data.success && data.data?.accessToken) {
        localStorage.setItem('accessToken', data.data.accessToken);
        return data.data.accessToken as string;
      }

      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Clear tokens on refresh failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Get a valid (non-expired) access token, refreshing if necessary
 */
export async function getValidAccessToken(): Promise<string | null> {
  let token = localStorage.getItem('accessToken');
  if (!token || isJwtExpired(token)) {
    token = await refreshAccessToken();
  }
  return token;
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  let token = await getValidAccessToken();

  // Build full URL - normalize and check if URL is already absolute
  let fullUrl: string;
  let trimmedUrl = url.trim();
  
  // First, detect and fix malformed URLs (e.g., "http://localhost:3000/http://localhost:3000/...")
  if (trimmedUrl.includes('http://') && trimmedUrl.split('http://').length > 2) {
    // Double URL detected - extract the last valid URL
    const matches = trimmedUrl.match(/https?:\/\/[^\s]+/g);
    if (matches && matches.length > 0) {
      trimmedUrl = matches[matches.length - 1]; // Use the last (most complete) URL
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG) {
        console.debug('[authenticatedFetch] Fixed double URL, using:', trimmedUrl);
      }
    }
  }
  
  // Also check for malformed URLs starting with port number (e.g., ":3000/http://...")
  if (trimmedUrl.match(/^:\d+\//)) {
    const match = trimmedUrl.match(/https?:\/\/[^\s]+/);
    if (match) {
      trimmedUrl = match[0];
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG) {
        console.debug('[authenticatedFetch] Fixed malformed URL, using:', trimmedUrl);
      }
    }
  }
  
  // Now check if URL is absolute
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    // URL is absolute - but in dev mode, we should extract the path and use relative URL
    if (import.meta.env.DEV) {
      // Extract the path from absolute URL for dev mode (use Vite proxy)
      try {
        const urlObj = new URL(trimmedUrl);
        fullUrl = urlObj.pathname + urlObj.search + urlObj.hash;
        // Debug log only - absolute URLs are automatically converted in dev mode
        if (import.meta.env.DEV && import.meta.env.VITE_DEBUG) {
          console.debug('[authenticatedFetch] Dev mode: converted absolute URL to relative:', fullUrl);
        }
      } catch (e) {
        // If URL parsing fails, try to extract path manually
        const pathMatch = trimmedUrl.match(/https?:\/\/[^\/]+(\/.*)/);
        fullUrl = pathMatch ? pathMatch[1] : trimmedUrl;
        if (import.meta.env.DEV && import.meta.env.VITE_DEBUG) {
          console.debug('[authenticatedFetch] Dev mode: extracted path from absolute URL:', fullUrl);
        }
      }
    } else {
      // Production: use absolute URL as-is
      fullUrl = trimmedUrl;
    }
  } else {
    // URL is relative
    if (import.meta.env.DEV) {
      // Dev mode: use relative URL (Vite proxy will handle it)
      fullUrl = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    } else {
      // Production: prepend base URL
      const baseUrl = API_URL || 'http://localhost:3000';
      fullUrl = `${baseUrl}${trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`}`;
    }
  }
  
  // Final safety check: ensure no double URLs
  if (fullUrl.includes('http://') && fullUrl.split('http://').length > 2) {
    const matches = fullUrl.match(/https?:\/\/[^\s]+/g);
    if (matches && matches.length > 0) {
      fullUrl = matches[matches.length - 1];
      console.error('[authenticatedFetch] Fixed double URL in final URL:', fullUrl);
    }
  }
  
  // Only log in debug mode to reduce console noise
  if (import.meta.env.DEV && import.meta.env.VITE_DEBUG) {
    console.debug('[authenticatedFetch] Input URL:', url, '→ Final URL:', fullUrl);
  }

  // Make initial request
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
    Authorization: `Bearer ${token}`,
  };
  
  // Only set Content-Type if not already set and body is not FormData
  if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  let response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // If token expired, try to refresh
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    
    if (newToken) {
      // Retry the request with new token
      const retryHeaders: Record<string, string> = {
        ...(options.headers as Record<string, string> || {}),
        Authorization: `Bearer ${newToken}`,
      };
      
      if (!retryHeaders['Content-Type'] && !(options.body instanceof FormData)) {
        retryHeaders['Content-Type'] = 'application/json';
      }

      response = await fetch(fullUrl, {
        ...options,
        headers: retryHeaders,
      });
    } else {
      // Refresh failed, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
  }

  return response;
}
